import type { QuizResult } from '@/domain/result'
import type { ResultsRepository } from '@/repositories/ResultsRepository'

const STORAGE_KEY = 'cybertales.results'

function readAll(): QuizResult[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as QuizResult[]) : []
  } catch {
    return []
  }
}

function writeAll(items: QuizResult[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
  } catch {
    // localStorage unavailable — results simply won't persist across reloads.
  }
}

// Zero-cost implementation of ResultsRepository (Document 11 §4).
// CosmosResultsRepository (P5) implements the same interface against
// Cosmos DB via the production Episode/Quiz Service.
export class LocalResultsRepository implements ResultsRepository {
  async save(result: QuizResult): Promise<void> {
    const all = readAll()
    all.push(result)
    writeAll(all)
  }

  async getById(id: string): Promise<QuizResult | null> {
    return readAll().find((r) => r.id === id) ?? null
  }

  async listForUser(userId: string): Promise<QuizResult[]> {
    return readAll().filter((r) => r.userId === userId)
  }

  async listForEpisode(userId: string, episodeId: string): Promise<QuizResult[]> {
    return readAll().filter((r) => r.userId === userId && r.episodeId === episodeId)
  }

  async countAttempts(userId: string, episodeId: string): Promise<number> {
    return (await this.listForEpisode(userId, episodeId)).length
  }
}
